import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
// @ts-expect-error - amplify_outputs.json is generated by Amplify
import outputs from '../../amplify_outputs.json';

const SESSION_KEY = 'sbt_session_id';
const LAST_PATH_KEY = 'sbt_last_path';
const SESSION_STARTED_KEY = 'sbt_session_started';

const getSessionId = () => {
  if (typeof window === 'undefined') return 'server';
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = crypto?.randomUUID ? crypto.randomUUID() : `sbt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    window.localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return `sbt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }
};

const safeJson = (value: unknown) => {
  try {
    return JSON.stringify(value ?? {});
  } catch {
    return '{}';
  }
};

const getUtmParams = (search: string) => {
  const params = new URLSearchParams(search);
  const utm: Record<string, string> = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });
  return utm;
};

const isAmplifyConfigured = Boolean(outputs?.auth || outputs?.data || outputs?.storage);
const shouldTrack = () => import.meta.env.PROD && isAmplifyConfigured;

const getClient = () => {
  try {
    return generateClient<Schema>();
  } catch {
    return null;
  }
};

export const trackEvent = async (
  eventType: string,
  data: {
    path?: string;
    search?: string;
    title?: string;
    referrer?: string;
    arrivalMode?: 'hotel' | 'cruise';
    metadata?: Record<string, unknown>;
  } = {}
) => {
  if (!shouldTrack() || typeof window === 'undefined') return;
  const client = getClient();
  if (!client) return;
  try {
    const sessionId = getSessionId();
    await client.models.AnalyticsEvent.create({
      sessionId,
      eventType,
      path: data.path,
      search: data.search,
      title: data.title,
      referrer: data.referrer ?? document.referrer,
      arrivalMode: data.arrivalMode,
      userAgent: navigator.userAgent,
      screen: `${window.innerWidth}x${window.innerHeight}`,
      metadata: safeJson(data.metadata),
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.debug('Analytics event failed', err);
    }
  }
};

export const trackPageView = async (path: string, search: string, arrivalMode?: 'hotel' | 'cruise') => {
  if (!shouldTrack() || typeof window === 'undefined') return;
  const lastPath = window.sessionStorage.getItem(LAST_PATH_KEY);
  const fullPath = `${path}${search || ''}`;
  if (lastPath === fullPath) return;
  window.sessionStorage.setItem(LAST_PATH_KEY, fullPath);
  const utm = getUtmParams(search);
  await trackEvent('page_view', {
    path,
    search,
    title: document.title,
    arrivalMode,
    metadata: {
      utm,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
};

export const trackSessionStart = async () => {
  if (!shouldTrack() || typeof window === 'undefined') return;
  const started = window.sessionStorage.getItem(SESSION_STARTED_KEY);
  if (started) return;
  window.sessionStorage.setItem(SESSION_STARTED_KEY, '1');
  await trackEvent('session_start', {
    metadata: {
      referrer: document.referrer || 'Direct',
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
};
